import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Github, 
  Linkedin, 
  Mail, 
  GraduationCap,
  Briefcase,
  MapPin,
  Calendar,
  MessageCircle,
  Rocket,
  Heart,
  Brain
} from 'lucide-react';
import vvcast from '../../assets/vvpodcast.png';
import somos from '../../assets/Somos_Educação_logo.png'
import casaevideo from '../../assets/casa-e-video-logo.png'
import eleva from '../../assets/eleva-educaco_logo_2_preto.png'
import sys from '../../assets/LOGO-Sys-Manager-horizontal-COLOR.png'
import globo from '../../assets/Logo_globo_principal-laranja.png'

const About = () => {
  const personalInfo = {
    name: "Victor Vicente",
    age: "23 anos",
    location: "Rio de Janeiro, RJ",
    graduation: "Análise e Desenvolvimento de Sistemas",
    postGraduation: "Pós-graduação em IA com ênfase em Machine Learning",
    experience: "4+ anos no setor educacional",
    interests: ["Tecnologia", "Educação", "Python", "Inteligência Artificial", "Machine Learning", "Inovação"]
  };

  const socialLinks = [
    {
      icon: Github,
      label: "GitHub",
      url: "https://github.com/victorviccente",
      color: "hover:text-blue-400"
    },
    {
      icon: Linkedin,
      label: "LinkedIn",
      url: "https://linkedin.com/in/victorviccente",
      color: "hover:text-blue-400"
    },
    {
      icon: Mail,
      label: "Email",
      url: "mailto:victorviccentecontato@gmail.com",
      color: "hover:text-blue-400"
    }
  ];

  const companyLogos = [
    casaevideo,
    eleva,
    somos,
    sys,
    globo
  ];

  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const clone = carousel.querySelector('.logo-slide')?.cloneNode(true);
    if (clone) {
      carousel.appendChild(clone);
    }

    const animate = () => {
      if (carousel.scrollLeft >= carousel.scrollWidth / 2) {
        carousel.scrollLeft = 0;
      } else {
        carousel.scrollLeft += 1;
      }
      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      // Cleanup if needed
    };
  }, []);

  return (
    <section id="about" className="relative py-20">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-black via-blue-900/20 to-black" />
      
      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto text-center mb-12"
        >
          <motion.span 
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-sm mb-3"
            whileHover={{ scale: 1.05 }}
          >
            <MessageCircle className="w-4 h-4" />
            <span>Apresentação</span>
          </motion.span>
          
          <h2 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600 mb-4">
            Sobre Mim
          </h2>
          <p className="text-blue-300 text-base">
            Fundador da 2V Educação e Desenvolvedor de Software
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Profile Section */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-blue-900/10 backdrop-blur-sm rounded-lg p-6 border border-blue-800/50"
            >
              <div className="space-y-6">
                <div className="relative">
                  <div className="aspect-square w-40 mx-auto overflow-hidden rounded-2xl border-2 border-blue-500/30">
                    <motion.img 
                      src={vvcast}
                      alt="Victor Vicente" 
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.05 }}
                    />
                  </div>
                  <motion.div 
                    className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-blue-500 rounded-full p-2"
                    whileHover={{ scale: 1.1 }}
                  >
                    <Rocket className="w-4 h-4 text-white" />
                  </motion.div>
                </div>

                <div className="space-y-4">
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-white mb-1">{personalInfo.name}</h3>
                    <div className="flex items-center justify-center gap-2 text-sm text-blue-300">
                      <MapPin className="w-4 h-4" />
                      <span>{personalInfo.location}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-blue-900/20 text-center">
                      <Calendar className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                      <span className="text-sm text-blue-300">{personalInfo.age}</span>
                    </div>
                    <div className="p-3 rounded-lg bg-blue-900/20 text-center">
                      <Briefcase className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                      <span className="text-sm text-blue-300">{personalInfo.experience}</span>
                    </div>
                  </div>

                  <div className="p-4 rounded-lg bg-blue-900/20">
                    <div className="flex items-center gap-2 mb-2">
                      <GraduationCap className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-blue-300">{personalInfo.graduation}</span>
                    </div>
                  </div>

                  <div className="p-4 rounded-lg bg-blue-900/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-blue-300">{personalInfo.postGraduation}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {personalInfo.interests.map((interest, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 text-xs rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20"
                      >
                        {interest}
                      </span>
                    ))}
                  </div>

                  <div className="flex justify-center gap-3">
                    {socialLinks.map((link, index) => (
                      <motion.a
                        key={index}
                        href={link.url}
                        
                        rel="noopener noreferrer"
                        className={`p-2.5 rounded-lg bg-blue-900/20 text-blue-300 ${link.color} transition-colors duration-300`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <link.icon className="w-5 h-5" />
                      </motion.a>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Bio Section */}
<motion.div
  initial={{ opacity: 0, x: 20 }}
  whileInView={{ opacity: 1, x: 0 }}
  transition={{ duration: 0.5 }}
  viewport={{ once: true }}
  className="bg-blue-900/10 backdrop-blur-sm rounded-lg p-6 border border-blue-800/50"
>
  <div className="space-y-6">
    <div className="flex items-center gap-2">
      <Heart className="w-5 h-5 text-blue-400" />
      <h3 className="text-lg font-semibold text-white">Minha História</h3>
    </div>

    <div className="space-y-4 text-sm text-blue-300 leading-relaxed">
      <p>
        Sou um desenvolvedor apaixonado por tecnologia e educação, com uma sólida formação em 
        Análise e Desenvolvimento de Sistemas. Minha jornada profissional tem sido 
        principalmente focada no setor educacional, onde atuei por 4 anos no maior sistema de ensino do Brasil.
      </p>
      <p>
        Durante esse período, desenvolvi uma compreensão profunda dos desafios e oportunidades 
        na interseção entre educação e tecnologia. Essa experiência me permitiu entender como 
        a inovação tecnológica pode transformar positivamente o processo de aprendizagem.
      </p>
      <p>
        Atualmente, como fundador da 2V Educação, meu foco está em democratizar o conhecimento 
        em tecnologia, com ênfase especial em Python e Inteligência Artificial. Acredito que 
        estas áreas são fundamentais para o futuro da tecnologia e busco capacitar novos 
        profissionais a dominarem essas habilidades.
      </p>
      <p>
        Estou cursando uma pós-graduação em Inteligência Artificial com ênfase em Machine Learning,
        aprofundando meus conhecimentos para aplicá-los em projetos inovadores e no ensino.
      </p>
    </div>

    <motion.div 
      className="p-4 rounded-lg bg-gradient-to-r from-blue-500/10 to-blue-700/10 border border-blue-500/20"
      whileHover={{ scale: 1.02 }}
    >
      <p className="text-sm text-blue-300 italic">
        "Acredito que a educação e a tecnologia, especialmente Python e IA, são as ferramentas 
        mais poderosas para transformar vidas e criar um futuro melhor para todos."
      </p>
    </motion.div>
  </div>
</motion.div>
          </div>

          {/* Company Logos Carousel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mt-12"
          >
            <h3 className="text-xl font-semibold text-white mb-4 text-center">Empresas onde trabalhei</h3>
            <div 
              ref={carouselRef}
              className="overflow-hidden whitespace-nowrap"
            >
              <div className="logo-slide inline-flex space-x-8">
                {companyLogos.map((logo, index) => (
                  <div key={index} className="inline-block">
                    <img src={logo} alt={`Company ${index + 1}`} className="w-24 h-24 object-contain" />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;